import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Lock, LogIn, LoaderCircle } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("commpaq_token", data.token);
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Accesso non riuscito. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="flex min-h-screen items-center justify-center bg-forest px-6 pt-20">
      <Seo title="Area Riservata | COMMPAQ" description="Accesso riservato agli amministratori COMMPAQ." robots="noindex,nofollow" />
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <Link to="/" data-testid="login-logo" className="inline-flex items-center gap-3">
            <LogoMark size={44} testId="login-logo-mark" />
            <span className="font-display text-2xl font-extrabold tracking-tight text-bone">
              COMM<span className="text-moss">PAQ</span>
            </span>
          </Link>
          <p className="mt-4 flex items-center justify-center gap-2 text-sm uppercase tracking-[0.25em] text-sage">
            <Lock size={14} />
            Area Riservata
          </p>
        </div>

        <form
          data-testid="login-form"
          onSubmit={handleSubmit}
          className="rounded-2xl border border-bone/10 bg-bone p-8 shadow-2xl sm:p-10"
        >
          <div>
            <label htmlFor="login-email" className="text-xs uppercase tracking-[0.2em] text-forest/60">
              Email
            </label>
            <Input
              id="login-email"
              type="email"
              data-testid="login-email-input"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@commpaq.it"
              className="mt-3 rounded-none border-0 border-b border-forest/25 bg-transparent px-0 py-3 text-base shadow-none focus-visible:border-moss focus-visible:ring-0"
            />
          </div>
          <div className="mt-8">
            <label htmlFor="login-password" className="text-xs uppercase tracking-[0.2em] text-forest/60">
              Password
            </label>
            <Input
              id="login-password"
              type="password"
              data-testid="login-password-input"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-3 rounded-none border-0 border-b border-forest/25 bg-transparent px-0 py-3 text-base shadow-none focus-visible:border-moss focus-visible:ring-0"
            />
          </div>

          {error && (
            <p data-testid="login-error" className="mt-6 text-sm text-red-700">
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="mt-10 inline-flex w-full items-center justify-center gap-3 rounded-full bg-moss px-8 py-6 text-sm uppercase tracking-[0.15em] text-bone transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-forest disabled:opacity-60"
          >
            {loading ? (
              <LoaderCircle size={16} className="animate-spin" />
            ) : (
              <>
                Accedi
                <LogIn size={16} />
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login;
